import clsx from 'clsx'
import { html, nothing, unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { translate } from 'lit-i18n'

import { DdsElement } from '../../internal/dds-hu-element'
import { SizedMixin } from '../../internal/mixin/sizedMixin'
import styles from './progress.scss?inline'

const ProgressBaseWithSized = SizedMixin(DdsElement)

export type ProgressVariant = 'linear' | 'circular'

export type ProgressColor =
  | 'neutral'
  | 'brand'
  | 'negative'
  | 'positive'
  | 'inverted'

/**
 * `dap-ds-progress`
 * @summary Progress indicator component with linear and circular variants.
 * @element dap-ds-progress
 * @title - Progress
 *
 * @csspart base - The main progress container.
 * @csspart track - The progress track (background).
 * @csspart fill - The progress fill (bar for linear, circle for circular).
 * @csspart label - The progress label text.
 * @csspart percentage - The percentage text (circular variant only).
 * @csspart background - The background circle (circular variant only).
 *
 * @slot label - Custom label content for the progress indicator.
 *
 * @cssprop --dds-progress-track-color - Background color of the progress track (default: var(--dds-neutral-200))
 * @cssprop --dds-progress-fill-color-neutral - Fill color for neutral variant (default: var(--dds-icon-neutral-base))
 * @cssprop --dds-progress-fill-color-brand - Fill color for brand variant (default: var(--dds-icon-brand-subtle))
 * @cssprop --dds-progress-fill-color-negative - Fill color for negative variant (default: var(--dds-icon-negative-subtle))
 * @cssprop --dds-progress-fill-color-positive - Fill color for positive variant (default: var(--dds-icon-positive-subtle))
 * @cssprop --dds-progress-fill-color-inverted - Fill color for inverted variant (default: var(--dds-icon-neutral-inverted))
 * @cssprop --dds-progress-height-xs - Height for extra small linear progress (default: var(--dds-spacing-50))
 * @cssprop --dds-progress-height-sm - Height for small linear progress (default: var(--dds-spacing-100))
 * @cssprop --dds-progress-height-md - Height for medium linear progress (default: var(--dds-spacing-150))
 * @cssprop --dds-progress-height-lg - Height for large linear progress (default: var(--dds-spacing-200))
 * @cssprop --dds-progress-height-xl - Height for extra large linear progress (default: var(--dds-spacing-300))
 * @cssprop --dds-progress-height-xxl - Height for extra extra large linear progress (default: var(--dds-spacing-400))
 * @cssprop --dds-progress-diameter-xs - Diameter for extra small circular progress (default: var(--dds-spacing-600))
 * @cssprop --dds-progress-diameter-sm - Diameter for small circular progress (default: var(--dds-spacing-800))
 * @cssprop --dds-progress-diameter-md - Diameter for medium circular progress (default: var(--dds-spacing-1200))
 * @cssprop --dds-progress-diameter-lg - Diameter for large circular progress (default: var(--dds-spacing-1600))
 * @cssprop --dds-progress-diameter-xl - Diameter for extra large circular progress (default: var(--dds-spacing-2000))
 * @cssprop --dds-progress-diameter-xxl - Diameter for extra extra large circular progress (default: var(--dds-spacing-2400))
 * @cssprop --dds-progress-stroke-width - Stroke width for circular progress (default: var(--dds-spacing-200))
 * @cssprop --dds-progress-animation-duration - Duration of indeterminate animation (default: 1.5s)
 *
 * @property { 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' } size - The size of the progress indicator. Default is `sm`. See SizedMixin.
 * @property {string} sizeMap - Responsive size map (e.g. "md:lg"); see SizedMixin.
 */
@customElement('dap-ds-progress')
export default class DapDSProgress extends ProgressBaseWithSized {
  /**
   * The variant of the progress indicator.
   * @type {"linear" | "circular"}
   */
  @property({ type: String, reflect: true }) variant: ProgressVariant = 'linear'

  /**
   * The color variant of the progress indicator.
   * @type {"neutral" | "brand" | "negative" | "positive" | "inverted"}
   */
  @property({ type: String, reflect: true }) color: ProgressColor = 'brand'

  /**
   * Current progress value (0-100 for percentage, or 0-max).
   */
  @property({ type: Number }) value?: number

  /**
   * Maximum value for the progress indicator.
   */
  @property({ type: Number }) max = 100

  /**
   * Whether to show the progress as indeterminate (loading animation).
   */
  @property({ type: Boolean, reflect: true }) indeterminate = false

  /**
   * Whether to show percentage text in the center (circular variant only).
   */
  @property({ type: Boolean }) showPercentage = false

  /**
   * Label text for the progress indicator.
   */
  @property({ type: String }) label?: string

  /**
   * The aria-live politeness level for screen readers.
   * @type {"polite" | "assertive" | "off"}
   */
  @property({ type: String, attribute: 'aria-live' })
  ariaLive: 'polite' | 'assertive' | 'off' = 'polite'

  static readonly styles = unsafeCSS(styles)

  /** Maps effectiveSize from SizedMixin to progress visual size class (xs–lg; xxs maps to xs). */
  private get progressSize(): string {
    return this.effectiveSize === 'xxs' ? 'xs' : this.effectiveSize
  }

  /**
   * Calculate the percentage value (0-100).
   */
  private get percentage(): number {
    if (this.value === undefined || this.indeterminate) {
      return 0
    }
    return Math.min(100, Math.max(0, (this.value / this.max) * 100))
  }

  /**
   * Calculate the stroke-dashoffset for circular progress.
   * Circle circumference = 2πr where r = 45 (radius of circle with center at 50,50 in viewBox 0 0 100 100)
   */
  private get circleOffset(): number {
    const radius = 45
    const circumference = 2 * Math.PI * radius
    return circumference * (1 - this.percentage / 100)
  }

  /**
   * Render the linear progress variant.
   */
  private renderLinear() {
    return html`
      <div part="track" class="progress__track" role="presentation">
        <div
          part="fill"
          class="progress__fill"
          style=${this.indeterminate ? '' : `width: ${this.percentage}%`}></div>
      </div>
    `
  }

  /**
   * Render the circular progress variant.
   */
  private renderCircular() {
    const radius = 45
    const circumference = 2 * Math.PI * radius

    return html`
      <svg
        part="track"
        class="progress__svg"
        viewBox="0 0 100 100"
        role="presentation">
        <!-- Background circle -->
        <circle
          part="background"
          class="progress__circle progress__circle--background"
          cx="50"
          cy="50"
          r="${radius}"
          fill="none"
          stroke-width="8" />
        <!-- Progress circle -->
        <circle
          part="fill"
          class="progress__circle progress__circle--progress"
          cx="50"
          cy="50"
          r="${radius}"
          fill="none"
          stroke-width="8"
          stroke-dasharray="${circumference}"
          stroke-dashoffset="${this.indeterminate ? 0 : this.circleOffset}"
          transform="rotate(-90 50 50)" />
      </svg>
      ${this.showPercentage && !this.indeterminate
        ? html`
            <span part="percentage" class="progress__percentage">
              ${Math.round(this.percentage)}%
            </span>
          `
        : nothing}
    `
  }

  render() {
    const role = this.indeterminate ? 'status' : 'progressbar'
    const ariaBusy = this.indeterminate ? 'true' : 'false'

    return html`
      <div
        part="base"
        class=${clsx(
          'progress',
          `progress--${this.variant}`,
          `progress--${this.color}`,
          `progress--${this.progressSize}`,
          {
            'progress--indeterminate': this.indeterminate,
          },
        )}
        role=${role}
        aria-busy=${ariaBusy}
        aria-label=${this.ariaLabel ??
        this.label ??
        (this.indeterminate
          ? translate('progress.loading')
          : translate('progress.progress', {
              percentage: Math.round(this.percentage),
            }))}
        aria-live=${this.ariaLive}
        aria-valuenow=${this.indeterminate
          ? nothing
          : Math.round(this.percentage)}
        aria-valuemin=${this.indeterminate ? nothing : 0}
        aria-valuemax=${this.indeterminate ? nothing : 100}>
        ${this.label
          ? html`<div part="label" class="progress__label">${this.label}</div>`
          : nothing}
        <slot name="label"></slot>

        <div class="progress__indicator">
          ${this.variant === 'circular'
            ? this.renderCircular()
            : this.renderLinear()}
        </div>

        <!-- Screen reader announcements -->
        <div class="sr-only" aria-live=${this.ariaLive} aria-atomic="true">
          ${this.indeterminate
            ? translate('progress.loading')
            : translate('progress.progress', {
                percentage: Math.round(this.percentage),
              })}
        </div>
      </div>
    `
  }
}
